import { useState } from "react";
import { useConsultation } from "../components/Consultation";
import { Reveal, Tag, ArrowLink } from "../components/Shared";
import { IMAGES } from "../data/content";

const PANELS = [
  {
    id: "bridal",
    tag: "Bespoke Bridal",
    title: ["YOUR DAY.", "YOUR SIGNATURE."],
    copy: "Not just a bridal outfit. A piece of your story.",
    items: ["Bridal Lehengas", "Bridal Sarees", "Custom Blouses", "Groom / Menswear", "Family Occasion Wear"],
    cta: "Begin Your Bridal Journey",
    image: IMAGES.bridal1,
    action: "consult",
  },
  {
    id: "menswear",
    tag: "Menswear",
    title: ["CUT WITH", "CHARACTER."],
    copy: "Contemporary tailoring. Indian soul.",
    items: ["Bespoke Suits", "Ethnic Wear", "Wedding Wear", "Occasion Wear"],
    cta: "Explore Menswear",
    image: IMAGES.mens1,
    pos: "object-[50%_80%]",
    action: "link",
  },
  {
    id: "kidswear",
    tag: "Kidswear",
    title: ["LITTLE ICONS.", "BIG PERSONALITY."],
    copy: "The house's smallest silhouettes carry its biggest attitude.",
    items: ["Occasion Wear", "Festive Edits", "Custom Pieces"],
    cta: "Discover Kidswear",
    image: IMAGES.kids1,
    action: "link",
  },
];

const Panel = ({ p, active, onEnter, onLeave }) => {
  const { open } = useConsultation();
  return (
    <div
      className={`always-dark group relative cursor-pointer overflow-hidden transition-all duration-700 ease-out ${
        active === p.id ? "md:flex-[2.2]" : "md:flex-1"
      } h-[70vh] md:h-[80vh]`}
      onMouseEnter={() => onEnter(p.id)}
      onMouseLeave={onLeave}
      data-testid={`category-panel-${p.id}`}
    >
      <img src={p.image} alt={`${p.tag} — ${p.copy}`} loading="lazy" className={`absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105 ${p.pos || "object-top"}`} />
      <div className={`absolute inset-0 transition-colors duration-700 ${active === p.id ? "bg-ink/45" : "bg-ink/65"}`} />
      <div className="relative flex h-full flex-col justify-end p-8 sm:p-10">
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-flame">{p.tag}</p>
        <h3 className="mt-3 font-serif text-4xl font-light leading-[1.02] tracking-tight sm:text-5xl">
          {p.title[0]}<br />{p.title[1]}
        </h3>
        <div className={`grid transition-all duration-700 ${active === p.id ? "mt-5 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0 md:mt-0"} mt-5 md:grid-rows-[0fr] md:opacity-0 md:group-hover:mt-5 md:group-hover:grid-rows-[1fr] md:group-hover:opacity-100`}>
          <div className="overflow-hidden">
            <p className="max-w-xs text-sm font-light italic text-bone/80">{p.copy}</p>
            <ul className="mt-4 space-y-1.5">
              {p.items.map((it) => (
                <li key={it} className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-bone/60">
                  <span className="h-px w-3 bg-flame" /> {it}
                </li>
              ))}
            </ul>
            <div className="mt-6">
              {p.action === "consult" ? (
                <ArrowLink testId={`category-cta-${p.id}`} onClick={open}>{p.cta}</ArrowLink>
              ) : (
                <ArrowLink to="/collections" testId={`category-cta-${p.id}`}>{p.cta}</ArrowLink>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Categories = () => {
  const [active, setActive] = useState("bridal");
  return (
    <section className="bg-coal py-28 md:py-40" data-testid="categories-section">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-10">
        <Reveal><Tag>The Occasions</Tag></Reveal>
        <h2 className="mt-6 font-serif text-5xl font-light tracking-tight sm:text-6xl lg:text-7xl">
          EVERY STITCH <span className="text-fog">HAS A STORY.</span>
        </h2>
      </div>
      <div className="mt-16 flex flex-col gap-2 px-2 md:flex-row">
        {PANELS.map((p) => (
          <Panel key={p.id} p={p} active={active} onEnter={setActive} onLeave={() => setActive(null)} />
        ))}
      </div>
    </section>
  );
};

export default Categories;
